import {MutableRefObject, useEffect, useRef, useState} from 'react';
import useLocalStorageState from 'use-local-storage-state';
import {
  DEFAULT_CONFIGURATOR_OPTIONS,
  ConfiguratorOptions,
  ConfiguratorV2Options,
  DEFAULT_V2_CONFIGURATOR_OPTIONS,
} from './storeConfigurator';

import {StoreRegionData} from './storeGeoLocator';
import {StoreCoordinatesData} from './storeCoordinates';
import {
  STORE_CONFIGURATOR_KEY,
  getLocalStorageConfiguratorV2Data,
  getLocalStorageLastUidCheck,
  getLocalStorageUid,
  setLocalStorageLastUidCheck,
} from './localStorage';
import {getUserIdForEmailOrPhone} from './firebase';
import {handleSetUserId} from './events';

const UID_CHECK_INTERVAL = 60 * 1000 * 10;

export const useLocalStorageProductConfiguratorState = () => {
  const [data, setData] = useLocalStorageState<ConfiguratorOptions>(
    'store-configurator-data',
    {defaultValue: DEFAULT_CONFIGURATOR_OPTIONS}
  );

  return [data, setData] as const;
};

export const useLocalStorageProductConfiguratorV2State = () => {
  const [data, setData] = useLocalStorageState<ConfiguratorV2Options>(
    STORE_CONFIGURATOR_KEY,
    {defaultValue: DEFAULT_V2_CONFIGURATOR_OPTIONS}
  );

  return [data, setData] as const;
};

/**
 * WARNING: This the data will be undefined on first render, and will update on a render
 * at a later state. If any data is to be used instantly on page load, such as with useEffect(() => {// code},[]),
 * you must set a timeout you must track these changes appropriately. Se Layout.tsx for inspiration.
 */
export const useStoreRegionCurrency = () => {
  const [data, setData] =
    useLocalStorageState<StoreRegionData[]>('store-country-data');

  return [data, setData] as const;
};

/**
 * WARNING: This the data will be undefined on first render, and will update on a render
 * at a later state. If any data is to be used instantly on page load, such as with useEffect(() => {// code},[]),
 * you must set a timeout you must track these changes appropriately. Se Layout.tsx for inspiration.
 */
export const useStoreCoordinates = () => {
  const [data, setData] = useLocalStorageState<StoreCoordinatesData>(
    'store-coordinates-data'
  );

  return [data, setData] as const;
};

export const useInterval = (callback: () => any, delay: number) => {
  const savedCallback = useRef<() => any>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback?.current) {
        savedCallback.current();
      }
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return () => undefined;
  }, [delay]);
};

export const useHover = <T>(): [MutableRefObject<T>, boolean] => {
  const [value, setValue] = useState<boolean>(false);
  const ref: any = useRef<T | null>(null);
  const handleMouseOver = (): void => setValue(true);
  const handleMouseOut = (): void => setValue(false);

  useEffect(() => {
    const node: any = ref.current;

    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);
      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
    return () => undefined;
  }, [ref.current]);

  return [ref, value];
};

/**
 * This hook checks the inputed email in product configurator and checks if
 * it belongs to a user. If it belongs to a user, we store it in LocalStorage
 * which will in turn be used for analytics
 */
export const useUserIdentification = async () => {
  const lastCheck = getLocalStorageLastUidCheck();
  const now = Date.now();

  if (now - lastCheck < UID_CHECK_INTERVAL) return;
  setLocalStorageLastUidCheck(now);

  const uid = getLocalStorageUid();
  const configuration = getLocalStorageConfiguratorV2Data();
  const email = configuration?.email;

  // No need to continue if we have or can't get the uid
  if (uid || !email) return;

  const emailRes = await getUserIdForEmailOrPhone(email);
  const foundUid = emailRes.data?.id;
  if (foundUid) {
    handleSetUserId(foundUid, email);
  }
};
